import {Text} from 'src/design/atoms/Text';
import type {ReactNode} from 'react';
import {useCallback, useDeferredValue, useEffect, useMemo, useRef} from 'react';
import type {TreeResponse} from 'src/api/ProactApiClient';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {TreeBrowserUnavailableAlert} from 'src/design/molecules/TreeBrowserUnavailableAlert';
import {BrowserLayout} from 'src/design/organisms/BrowserLayout';
import type {HierarchyTreeNode} from 'src/design/organisms/HierarchyTree';
import {TreeBrowserPane} from 'src/design/organisms/TreeBrowserPane';
import {findTreeNode} from 'src/shared/utils/treeUtils';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {TreeNodeIconsByKind} from './treeBrowserUtils';
import {toHierarchyTreeNodes} from './treeBrowserUtils';
import {useTreeBrowser} from './useTreeBrowser';

export interface TreeResponseQueryResult {
  currentData?: TreeResponse[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: unknown;
}

export interface TreeResponseBrowserProps {
  query: TreeResponseQueryResult;
  rootId: string;
  rootLabel: string;
  /** Pane heading, drawer title, and base of the toggle `aria-label`s. */
  paneTitle: string;
  /** Namespaces expansion state shared across the module's routes. */
  expansionKey: string;
  /** Undefined until the page resolves its record; the last selection holds until then. */
  selectedId?: string;
  /** `node` is undefined for the synthetic root; read `node.kind` to branch. */
  onSelect: (id: string, node?: TreeResponse) => void;
  icon: ReactNode;
  iconsByKind?: TreeNodeIconsByKind;
  searchPlaceholder: string;
  emptyText: string;
  unavailableItemName: string;
  unavailableLabel: string;
  resetLabel: string;
  children: ReactNode;
}

/** Shared app shell that adapts a `TreeResponse` query to the tree browser organisms. */
export const TreeResponseBrowser = ({
  query,
  rootId,
  rootLabel,
  paneTitle,
  expansionKey,
  selectedId,
  onSelect,
  icon,
  iconsByKind,
  searchPlaceholder,
  emptyText,
  unavailableItemName,
  unavailableLabel,
  resetLabel,
  children,
}: TreeResponseBrowserProps) => {
  const {
    effectiveSelectedId,
    expandedIds,
    setExpandedIds,
    search,
    setSearch,
    expandTo,
  } = useTreeBrowser({
    expansionKey,
    defaultExpandedIds: [rootId],
    selectedId,
    rootId,
  });
  const branchNodes = useMemo(
    () => toHierarchyTreeNodes(query.currentData ?? [], icon, iconsByKind),
    [icon, iconsByKind, query.currentData]
  );
  const nodes = useMemo<HierarchyTreeNode[]>(
    () => [
      {
        id: rootId,
        label: rootLabel,
        icon,
        children: branchNodes.length > 0 ? branchNodes : undefined,
      },
    ],
    [branchNodes, icon, rootId, rootLabel]
  );

  useEffect(() => {
    expandTo(nodes, effectiveSelectedId);
  }, [expandTo, nodes, effectiveSelectedId]);

  // The pane repaints a large tree; deferring its selection lets the page commit and fetch first.
  const paneSelectedId = useDeferredValue(effectiveSelectedId);

  const selectRef = useRef({onSelect, data: query.currentData});
  selectRef.current = {onSelect, data: query.currentData};
  const handleSelect = useCallback(
    (id: string) =>
      selectRef.current.onSelect(
        id,
        id === rootId
          ? undefined
          : findTreeNode(id, selectRef.current.data ?? [])
      ),
    [rootId]
  );

  const selectedLabel = useMemo(
    () =>
      effectiveSelectedId === rootId
        ? rootLabel
        : findTreeNode(effectiveSelectedId, query.currentData ?? [])?.label,
    [query.currentData, rootId, rootLabel, effectiveSelectedId]
  );
  // A node created during a refetch is absent from the stale tree, so the alert waits for it.
  const selectionUnavailable =
    !query.isLoading &&
    !query.isFetching &&
    !query.isError &&
    effectiveSelectedId !== rootId &&
    !selectedLabel;
  const selectionLabel =
    selectedLabel ??
    (query.isLoading || query.isFetching
      ? 'Loading selection'
      : `${unavailableLabel} (${effectiveSelectedId})`);
  // Only a fetch with nothing cached blanks the tree; a refetch keeps the stale tree on screen.
  const isPending =
    query.currentData === undefined && (query.isLoading || query.isFetching);
  const pane = (
    <TreeBrowserPane
      title={paneTitle}
      nodes={nodes}
      selectedId={paneSelectedId}
      onSelect={handleSelect}
      expandedIds={expandedIds}
      onExpandedChange={setExpandedIds}
      searchable={branchNodes.length > 0}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder={searchPlaceholder}
      isLoading={isPending}
      error={
        query.isError ? (
          <ErrorAlert message={parseErrorMessage(query.error)} mt={0} />
        ) : undefined
      }
      header={
        selectionUnavailable ? (
          <TreeBrowserUnavailableAlert
            itemName={unavailableItemName}
            resetLabel={resetLabel}
            onReset={() => onSelect(rootId)}
          />
        ) : !isPending && !query.isError && branchNodes.length === 0 ? (
          <Text size="sm" c="dimmed">
            {emptyText}
          </Text>
        ) : undefined
      }
    />
  );

  return (
    <BrowserLayout
      pane={pane}
      paneTitle={paneTitle}
      selectionLabel={selectionLabel}
      selectionKey={effectiveSelectedId}>
      {children}
    </BrowserLayout>
  );
};
